// Category tree helpers. Tally allows arbitrary nesting in the model, but the
// UI caps depth at MAX_LEVELS (1-indexed: top-level = 1, deepest allowed = 4).

export const MAX_LEVELS = 4

function indexById(all) {
  const m = new Map()
  for (const c of all) m.set(c.id, c)
  return m
}

// 0-indexed depth from root (top-level = 0)
export function getDepth(catId, all) {
  const byId = indexById(all)
  const seen = new Set()
  let depth = 0
  let cur = byId.get(catId)
  while (cur?.parentId && !seen.has(cur.id)) {
    seen.add(cur.id)
    cur = byId.get(cur.parentId)
    if (!cur) break
    depth++
  }
  return depth
}

// IDs of all descendants of catId (recursive)
export function getDescendantIds(catId, all) {
  const out = new Set()
  function walk(id) {
    for (const c of all) {
      if (c.parentId === id && !out.has(c.id)) {
        out.add(c.id)
        walk(c.id)
      }
    }
  }
  walk(catId)
  return out
}

// Depth of the subtree rooted at catId (0 = no children, 1 = has children only, etc.)
export function getSubtreeDepth(catId, all) {
  let max = 0
  function walk(id, d) {
    if (d > max) max = d
    for (const c of all) {
      if (c.parentId === id) walk(c.id, d + 1)
    }
  }
  walk(catId, 0)
  return max
}

// Array of ancestor categories (root → ... → cat's direct parent)
export function getAncestors(catId, all) {
  const byId = indexById(all)
  const out = []
  const seen = new Set()
  let cur = byId.get(catId)
  while (cur?.parentId && !seen.has(cur.id)) {
    seen.add(cur.id)
    const parent = byId.get(cur.parentId)
    if (!parent) break
    out.unshift(parent)
    cur = parent
  }
  return out
}

// Full path string from root → cat using "/" separator. Optional icon prefix.
export function getCategoryPath(cat, all, { withIcon = false } = {}) {
  const chain = [...getAncestors(cat.id, all), cat]
  return chain.map(c => withIcon && c.icon ? `${c.icon} ${c.name}` : c.name).join(' / ')
}

// Can `draggedId` be reparented under `targetId` without violating MAX_LEVELS or cycles?
// targetId === null means "move to top-level".
export function canReparent(draggedId, targetId, all) {
  if (draggedId === targetId) return false
  if (targetId === null) {
    const dragged = all.find(c => c.id === draggedId)
    return Boolean(dragged?.parentId) // only meaningful if currently nested
  }
  const target = all.find(c => c.id === targetId)
  const dragged = all.find(c => c.id === draggedId)
  if (!target || !dragged) return false
  if (target.type !== dragged.type) return false
  if (dragged.parentId === target.id) return false // already child of target
  // No cycles: target cannot be a descendant of dragged
  const draggedDescendants = getDescendantIds(draggedId, all)
  if (draggedDescendants.has(targetId)) return false
  // Depth budget: depth(target) + 1 (dragged's new depth) + subtree(dragged) ≤ MAX_LEVELS - 1 (0-indexed)
  const targetDepth   = getDepth(targetId, all)
  const draggedSubtree = getSubtreeDepth(draggedId, all)
  return (targetDepth + 1 + draggedSubtree) <= (MAX_LEVELS - 1)
}

// Build a flat ordered list { cat, depth, hasChildren } from a filtered set of categories
// (e.g. all categories of one type). Walks the tree depth-first; orphans (parentId pointing
// to a missing/wrong-type cat) are surfaced as top-level subtrees so they stay editable.
//
// Options:
//   expanded — optional Set<id> of parents whose children should be emitted. If omitted,
//              all subtrees are walked. If provided, collapsed parents emit themselves
//              but not their descendants.
// Categorie di sistema: create e mantenute da una sync (oggi solo "Even"),
// non dall'utente. Restano sulle transazioni ma spariscono dalla gestione:
// rinominarle o cancellarle romperebbe solo la sync, che le ricreerebbe.
// Il marchio `source` è quello messo da even-sync; il confronto sul nome è
// il ripiego per le categorie nate prima che il marchio esistesse.
export function isSystemCategory(c) {
  return c?.source === 'even' || (c?.name === 'Even' && c?.type === 'expense')
}

export function buildTreeRows(filtered, { expanded = null } = {}) {
  const byParent = new Map()
  for (const c of filtered) {
    const k = c.parentId ?? null
    if (!byParent.has(k)) byParent.set(k, [])
    byParent.get(k).push(c)
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name))

  const allIds = new Set(filtered.map(c => c.id))
  const out = []
  const visited = new Set()

  function shouldRecurse(catId, hasChildren) {
    if (!hasChildren) return true
    if (expanded === null) return true
    return expanded.has(catId)
  }

  function emit(parentId, depth) {
    for (const c of byParent.get(parentId) ?? []) {
      if (visited.has(c.id)) continue
      visited.add(c.id)
      const hasChildren = (byParent.get(c.id)?.length ?? 0) > 0
      out.push({ cat: c, depth, hasChildren })
      if (shouldRecurse(c.id, hasChildren)) emit(c.id, depth + 1)
    }
  }

  emit(null, 0)
  // Surface orphan subtrees as top-level
  for (const c of filtered) {
    if (visited.has(c.id)) continue
    if (c.parentId && !allIds.has(c.parentId)) {
      visited.add(c.id)
      const hasChildren = (byParent.get(c.id)?.length ?? 0) > 0
      out.push({ cat: c, depth: 0, hasChildren })
      if (shouldRecurse(c.id, hasChildren)) emit(c.id, 1)
    }
  }
  return out
}
